import { faBell } from '@fortawesome/pro-solid-svg-icons/faBell';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons/faEnvelope';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Dialog, DialogContent, Grid, Menu, MenuItem, Tab, Tabs, Typography } from '@mui/material';
import { StyledComponentProps } from "@mui/styles";
import * as api from "api/Core";
import { FeedClassKey } from "assets/jss/common-styles";
import { HeaderStylesClassKey } from 'assets/jss/layouts/headerLinksStyle';
import { AppBarTabs } from 'components/AppTabs';
import { RequestIndicator, SpinnerIndicator } from 'components/CallServer';
import IconCaretDown from 'components/Icons/IconCaretDown';
import MuiMessageBox from 'components/MuiExtras/MessageBox/MuiMessageBox';
import MuiClickable from 'components/MuiExtras/MuiClickable';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiDialogTitleClose from 'components/MuiExtras/MuiDialogTitleClose';
import MuiColor, { MuiColors } from 'components/MuiExtras/Typography/MuiColor';
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import AppMessage from 'components/Translations/AppMessage';
import AppResource from 'components/Translations/AppResources';
import NotificationEmailOptionName from 'components/Translations/NotificationEmailOptionName';
import NotificationMainGroupTypeName from 'components/Translations/NotificationMainGroupTypeName';
import NotificationSubGroupTypeName from 'components/Translations/NotificationSubGroupTypeName';
import NotificationTypeName from 'components/Translations/NotificationTypeName';
import { NotificationEmailOption, NotificationMainGroupType, NotificationSubGroupType, NotificationType } from 'models/ApiEnums';
import { NotificationSetupDto, NotificationSetupItemDto } from 'models/ApiModels';
import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    operation: "none" | "loading" | "updating";
    data: NotificationSetupDto;
    selectedTab: number;
    anchorEl: any;
    lastError: any;
}

interface IProps {
    onClose: () => void;
}

interface GroupNameCollection {
    mainGroupType: NotificationMainGroupType;
    text: React.ReactNode;
}

interface EmailOptionCollection {
    value: NotificationEmailOption;
    label: string;
}

type Props = IProps & WithSnackbar & WithTranslation & StyledComponentProps<FeedClassKey> & StyledComponentProps<HeaderStylesClassKey>;

const excludeImmediateOption = [NotificationType.NewCourses];

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class NotificationConfigDrawer extends React.PureComponent<Props, IState> {
    private tabNames: GroupNameCollection[] = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            operation: "loading",
            selectedTab: null,
            anchorEl: null,
            lastError: null,
            data: null,
        };
    }

    private getConfigOptions() {

        api.getUserNotificationConfig()
            .then((result) => {
                this.tabNames = this.getTabNames(result);

                this.setState(
                    {
                        data: result,
                        operation: "none",
                        selectedTab: this.tabNames[0].mainGroupType
                    }
                );
            })
            .catch((error) => {
                this.setState({ operation: "none", lastError: error });
            });
    }


    private getTabNames(data: NotificationSetupDto): GroupNameCollection[] {
        var groups: Array<GroupNameCollection> = [];
        data.mainGroups.forEach((v) => {
            groups.push(
                {
                    mainGroupType: v.mainGroupType,
                    text: <NotificationMainGroupTypeName mainGroupType={v.mainGroupType} />
                }
            );
        });
        return groups;
    }

    private getEmailOptionpNames(): Array<EmailOptionCollection> {
        var list: Array<EmailOptionCollection> = [];
        list.push(
            {
                value: NotificationEmailOption.None,
                label: this.props.t(`NotificationEmailOption.${NotificationEmailOption[NotificationEmailOption.None]}`)
            }
        );

        list.push(
            {
                value: NotificationEmailOption.Immediate,
                label: this.props.t(`NotificationEmailOption.${NotificationEmailOption[NotificationEmailOption.Immediate]}`)
            }
        );

        list.push(
            {
                value: NotificationEmailOption.Resume,
                label: this.props.t(`NotificationEmailOption.${NotificationEmailOption[NotificationEmailOption.Resume]}`)
            }
        );

        return list;
    }

    private updateConfig(subGroupType: NotificationSubGroupType, notificationType: NotificationType, blockNotification: boolean, emailOption: NotificationEmailOption) {

        this.setState({ anchorEl: null });

        var dto: NotificationSetupItemDto = {
            subGroupType: subGroupType,
            notificationType: notificationType,
            options: {
                blockNotification: blockNotification,
                emailOption: emailOption
            }
        };

        api.updateUserNotificationConfiguration(dto)
            .then(() => {

                const newData = Object.assign({}, this.state.data);
                var mainGroup = newData.mainGroups.find(p => p.mainGroupType == this.state.selectedTab);
                if (mainGroup) {
                    var item = mainGroup.items.find(p => p.subGroupType == subGroupType && p.notificationType == notificationType);
                    if (item) {
                        if (blockNotification != null) item.options.blockNotification = blockNotification;
                        if (emailOption != null) item.options.emailOption = emailOption;
                    }
                    this.setState({ data: newData });
                }

            })
            .catch((error) => {
                if (!error.handled) {
                    this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                }
            });
    }

    private handleTabChange = (event: any, value: any) => {
        this.setState({ selectedTab: value });
    };

    private handleCloseMenu = () => {
        this.setState({ anchorEl: null });
    };

    private handleClickListItem = (event: any) => {
        this.setState({ anchorEl: event.currentTarget });
    };

    private renderTabs() {

        return (
            <AppBar position="static" color="secondary" >
                <AppBarTabs
                    value={this.state.selectedTab}
                    onChange={this.handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {
                        this.tabNames.map((v) =>
                            <Tab
                                label={v.text}
                                key={v.mainGroupType}
                                value={v.mainGroupType}
                                className={this.props.classes.dropdownTabRoot} />
                        )
                    }
                </AppBarTabs>
            </AppBar>
        );
    }

    private buildId(v: NotificationSetupItemDto) {
        return v.subGroupType != null
            ? `s_${v.subGroupType}`
            : `n_${v.notificationType}`;
    }

    private renderSelectedTab() {

        const emailOptions = this.getEmailOptionpNames();

        return this.state.data.mainGroups.filter(x => x.mainGroupType == this.state.selectedTab)[0].items.map((v, index) => {

            var bellcolor: MuiColors = v.options.blockNotification
                ? "muted"
                : "warning";

            var bellMessage = v.options.blockNotification
                ? <AppResource resource="Views.Notification.NotificationConfigDrawer.NotShowNotification" />
                : <AppResource resource="Views.Notification.NotificationConfigDrawer.ShowNotification" />;

            var emailColor: MuiColors = v.options.emailOption == NotificationEmailOption.None
                ? "muted"
                : "warning";

            var emailMessage = <NotificationEmailOptionName emailOption={v.options.emailOption} />;

            return (
                <MuiContainer key={index} margin="normal">
                    <Typography variant="button">
                        {
                            v.subGroupType == null
                                ? <NotificationTypeName notificationType={v.notificationType} />
                                : <NotificationSubGroupTypeName subGroupType={v.subGroupType} />
                        }
                    </Typography>

                    <Grid container justifyContent="space-between" >
                        <Grid item>

                            <MuiClickable onClick={() => this.updateConfig(v.subGroupType, v.notificationType, !v.options.blockNotification, null)}>
                                <MuiColor variant={bellcolor}>
                                    <FontAwesomeIcon icon={faBell} fixedWidth />
                                    {bellMessage}
                                </MuiColor>
                            </MuiClickable>
                        </Grid>

                        <Grid item>

                            <MuiClickable id={this.buildId(v)} onClick={this.handleClickListItem}>
                                <MuiColor variant={emailColor}>
                                    <FontAwesomeIcon icon={faEnvelope} fixedWidth />
                                    {emailMessage}
                                    <span>&nbsp;<IconCaretDown /></span>
                                </MuiColor>
                            </MuiClickable>

                            <Menu
                                anchorEl={this.state.anchorEl}
                                open={this.state.anchorEl != null && this.state.anchorEl.id == this.buildId(v)}
                                onClose={this.handleCloseMenu}
                            >
                                {
                                    emailOptions.map(option => {
                                        return option.value == NotificationEmailOption.Immediate && excludeImmediateOption.includes(v.notificationType)
                                            ? null
                                            : (
                                                <MenuItem
                                                    key={option.value}
                                                    value={option.value}
                                                    selected={option.value == v.options.emailOption}
                                                    onClick={
                                                        (e) => {
                                                            this.updateConfig(v.subGroupType, v.notificationType, null, option.value);
                                                        }
                                                    }
                                                >
                                                    {option.label}
                                                </MenuItem>
                                            );
                                    })
                                }
                            </Menu>
                        </Grid>
                    </Grid>

                </MuiContainer >
            );
        });
    }

    componentDidMount() {
        this.getConfigOptions();
    }

    public render() {
        const { onClose } = this.props;

        return (
            <Dialog
                open
                fullWidth
                maxWidth="md"
                onBackdropClick={onClose}
                onClose={onClose}
            >
                <MuiDialogTitleClose
                    onClose={this.props.onClose}
                >
                    <AppResource resource="Views.Notification.NotificationConfigDrawer.DialogTitle" />
                </MuiDialogTitleClose>

                <DialogContent>
                    {
                        this.state.lastError != null
                            ? <MuiMessageBox variant="error" message={this.state.lastError} />
                            : this.state.operation == "loading" && this.state.data == null
                                ? <SpinnerIndicator />
                                : <>
                                    {this.state.operation == "updating" && <RequestIndicator type="post" />}

                                    <MuiContainer margin="normal">
                                        {this.renderTabs()}
                                    </MuiContainer>

                                    <div style={{ height: 300, overflow: "auto" }}>
                                        {this.renderSelectedTab()}
                                    </div>
                                </>
                    }
                </DialogContent>
            </Dialog>
        );
    }
}

export default withTranslation(["sys"])(withSnackbar(NotificationConfigDrawer));